/**
 * OpenNebula networking. Phase 1 covers the per-instance NIC surface
 * (list networks for the attach picker + attach/detach a NIC). The standalone
 * networks/VPC/VPN management lands in Phase 2 and will extend this module.
 *
 * Endpoints (v2 s10003): /networks, /instances/:id/networks/attach,
 * /instances/:id/networks/detach.
 */
import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApi, onPath, onKeys, unwrap } from './_shared';

export interface Network {
  id: string;
  name?: string;
  /** CIDR — the API field is `prefix` (e.g. "10.10.22.0/24"). */
  prefix?: string;
  cidr?: string;
  description?: string;
  uid?: string;
  vlan_id?: string;
  type?: string;
  address?: string;
  /** Reservation start address (AR `ip`); reserved-network rows carry this. */
  ip?: string;
  size?: number;
  created_at?: string;
}

/** A network's CIDR, tolerating either field name. */
export function networkCidr(n: Network): string | undefined {
  return n.prefix || n.cidr;
}

export function useNetworkList(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.networks(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      const body = unwrap<{ networks?: Network[] } | Network[]>(
        await serviceApi.get(onPath(serviceId!, 'networks')),
      );
      return Array.isArray(body) ? body : body.networks ?? [];
    },
  });
}

/** Per-network detail: attach state + reserved-gateway info. */
export interface NetworkSummary {
  name?: string;
  /** Instance ids currently attached to this network. */
  attached?: (string | number)[];
  size?: number;
  leased?: number;
  has_reserved_gateway?: boolean;
  reserved_gateway_ip?: string | null;
}

export function useNetworkSummary(serviceId: string | undefined, networkId: string | undefined) {
  return useQuery({
    queryKey: onKeys.network(serviceId ?? '', networkId ?? ''),
    enabled: !!serviceId && !!networkId,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      const body = unwrap<{ network?: NetworkSummary } | NetworkSummary>(
        await serviceApi.get(onPath(serviceId!, `networks/${networkId}`)),
      );
      return (body && typeof body === 'object' && 'network' in body ? body.network : body) as NetworkSummary;
    },
  });
}

/** A suggested next free private CIDR for creating a network. */
export interface NetworkIncremental {
  network_name?: string;
  /** Suggested CIDR (e.g. "10.0.2.0/24"). */
  network_incremental?: string;
}

export function usePrivateNetworkIncremental(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.networkIncremental(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async () =>
      unwrap<NetworkIncremental>(await serviceApi.get(onPath(serviceId!, 'private_network_incremental'))),
  });
}

export function useCreatePrivateNetwork(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) =>
      (await serviceApi.post(onPath(serviceId, 'networks'), payload)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.networks(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDeletePrivateNetwork(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await serviceApi.delete(onPath(serviceId, `networks/${id}`))).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.networks(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

// ---- Reserved networks ---------------------------------------------------

export function useReservedNetworks(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.reservedNetworks(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      // `reserves#index` wraps the list under `reserves` (NOT `networks`).
      const body = unwrap<{ reserves?: Network[] } | Network[]>(
        await serviceApi.get(onPath(serviceId!, 'networks/reserves')),
      );
      return Array.isArray(body) ? body : body.reserves ?? [];
    },
  });
}

export function useCreateReservedNetwork(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) =>
      (await serviceApi.post(onPath(serviceId, 'networks/reserves'), payload)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.reservedNetworks(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDeleteReservedNetwork(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await serviceApi.delete(onPath(serviceId, `networks/reserves/${id}`))).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.reservedNetworks(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

// ---- Virtual routers -----------------------------------------------------

/** An instance (VM) connected to the network behind a router NIC. */
export interface RouterNicInstance {
  id: string;
  name?: string;
  uid?: number;
  status?: string;
  ip?: string;
}

export interface RouterNic {
  nic_id?: string | number;
  /** Network display name (API field is `network_name`). */
  network_name?: string;
  network_id?: string;
  ip?: string;
  mac?: string;
  is_public?: boolean;
  /** VMs currently attached to this NIC's network (router detail payload). */
  instances?: RouterNicInstance[];
}

/** NAT settings read from the router VM's VNF context (detail payload). */
export interface RouterNatContext {
  nat4_enabled?: boolean;
  sdnat4_enabled?: boolean;
  /** Comma/space-separated interface list (e.g. "eth1 eth2"). */
  sdnat4_interfaces?: string;
  nat4_interfaces_out?: string;
}

export interface VirtualRouter {
  id: string;
  name?: string;
  /** Provisioning status (`"active"` once usable). */
  status?: string;
  description?: string;
  /** NICs are returned at the top level (list + detail), not under metadata. */
  nics?: RouterNic[];
  /** External NAT public IP assigned to the router (list + detail). */
  public_ip?: string;
  /** NAT4 / SDNAT4 config from the router's VNF context (detail only). */
  nat_context?: RouterNatContext;
  created_at?: string;
  metadata?: {
    resources?: { cores?: number; memory?: number };
    instance_id?: number;
  };
}

export interface RouterRoute {
  destination: string;
  via: string;
}

export function useVirtualRouters(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.routers(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      const body = unwrap<{ routers?: VirtualRouter[] } | VirtualRouter[]>(
        await serviceApi.get(onPath(serviceId!, 'routers')),
      );
      return Array.isArray(body) ? body : body.routers ?? [];
    },
  });
}

export function useVirtualRouter(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: onKeys.router(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ router?: VirtualRouter } | VirtualRouter>(
        await serviceApi.get(onPath(serviceId!, `routers/${id}`)),
      );
      return (body && typeof body === 'object' && 'router' in body ? body.router : body) as VirtualRouter;
    },
  });
}

export function useCreateVirtualRouter(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) =>
      (await serviceApi.post(onPath(serviceId, 'routers'), payload)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.routers(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDeleteVirtualRouter(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await serviceApi.delete(onPath(serviceId, `routers/${id}`))).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.routers(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useRouterRoutes(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: onKeys.routerRoutes(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ routes?: RouterRoute[] } | RouterRoute[]>(
        await serviceApi.get(onPath(serviceId!, `routers/${id}/routes`)),
      );
      return Array.isArray(body) ? body : body.routes ?? [];
    },
  });
}

export function useAddRoute(
  serviceId: string,
  routerId: string,
  options?: UseMutationOptions<unknown, unknown, RouterRoute>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RouterRoute) =>
      (await serviceApi.post(onPath(serviceId, `routers/${routerId}/routes`), payload)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.routerRoutes(serviceId, routerId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDeleteRoute(
  serviceId: string,
  routerId: string,
  options?: UseMutationOptions<unknown, unknown, { destination: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { destination: string }) =>
      (await serviceApi.delete(onPath(serviceId, `routers/${routerId}/routes`), { data: payload })).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.routerRoutes(serviceId, routerId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useAttachNic(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, { network_id: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { network_id: string }) =>
      (await serviceApi.post(onPath(serviceId, `instances/${instanceId}/networks/attach`), payload))
        .data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: onKeys.instance(serviceId, instanceId) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDetachNic(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, { network_id: number | string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    // s10003 contract: instance NIC-detach carries the NIC_ID under the
    // `network_id` body key (see @services networks_controller#detach, which
    // reads `params[:network_id]`). Sending any other key lands as nil→0
    // server-side and silently detaches NIC 0 instead of the chosen interface.
    mutationFn: async (payload: { network_id: number | string }) =>
      (await serviceApi.post(onPath(serviceId, `instances/${instanceId}/networks/detach`), payload))
        .data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: onKeys.instance(serviceId, instanceId) });
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Virtual-router network attachment -----------------------------------

export function useAttachRouterNetwork(
  serviceId: string,
  routerId: string,
  options?: UseMutationOptions<unknown, unknown, { network_id: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { network_id: string }) =>
      (await serviceApi.post(onPath(serviceId, `routers/${routerId}/attach_network`), payload)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.router(serviceId, routerId) });
      qc.invalidateQueries({ queryKey: onKeys.routers(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDetachRouterNetwork(
  serviceId: string,
  routerId: string,
  options?: UseMutationOptions<unknown, unknown, { nic_id: number | string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { nic_id: number | string }) =>
      (await serviceApi.post(onPath(serviceId, `routers/${routerId}/detach_network`), payload)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.router(serviceId, routerId) });
      qc.invalidateQueries({ queryKey: onKeys.routers(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}
